import React from "react";

const Privacy = () => {
  return (
    <div className="page">
      <h1>GH Tracker – Privacy Policy</h1>

      <p>
        <strong>Effective date:</strong> {new Date().toLocaleDateString()}
      </p>
      <p>
        <strong>Developer/Company:</strong> RedCalf
      </p>
      <p>
        <strong>Contact:</strong> privacy@GH Tracker.com
      </p>
      <p>
        <strong>Mailing address:</strong> [Your Company Address]
      </p>

      <h2>Quick Summary</h2>
      <p>
        GH Tracker is a growth-hormone tracking app. We collect a minimal set of
        non-identifying measurements to operate and improve the app. This policy
        explains what we collect, how we use it, and your choices.
      </p>

      <ul>
        <li>We collect age, height, weight, and dosage you enter.</li>
        <li>
          We generate a random, resettable app ID on your device to keep entries
          from the same device together over time.
        </li>
        <li>
          We do not collect or send: name, email, photos/voice, precise GPS
          location, or free-text notes.
        </li>
        <li>
          You can use <em>Reset My Data</em> anytime to rotate your app ID and
          request deletion of prior linked records.
        </li>
      </ul>

      <h2>Information We Collect</h2>

      <h3>Growth Entries (You Input)</h3>
      <ul>
        <li>
          Age (e.g., months or age bucket), height, weight, and GH dosage.
        </li>
        <li>Timestamp (e.g., day or week) used for trend calculations.</li>
        <li>
          For privacy, we may round or bucket values (e.g., age ranges,
          week-based timestamps).
        </li>
      </ul>

      <h3>Per-Install App Identifier (Random & Resettable)</h3>
      <ul>
        <li>
          A randomly generated ID stored locally (e.g., Keychain/Keystore) so
          entries from the same device can be linked over time.
        </li>
        <li>
          We do not use advertising IDs (IDFA/AAID) or platform marketing
          identifiers.
        </li>
      </ul>

      <h3>Minimal Technical Data (For Security & Reliability)</h3>
      <ul>
        <li>
          App version, OS version, basic diagnostics, and a transient IP address
          in server logs (briefly retained for security/anti-abuse, then deleted
          or truncated).
        </li>
      </ul>

      <h3>We Do Not Collect</h3>
      <ul>
        <li>
          name, email, phone number, contacts, calendars, photos, voice
          recordings, precise GPS, or free-text notes.
        </li>
      </ul>

      <h2>How We Use Information</h2>
      <ul>
        <li>App functionality: show your history, charts, and trends.</li>
        <li>
          Internal analytics & quality: measure feature usage and reliability to
          improve GH Tracker (no ad profiling or cross-app tracking).
        </li>
        <li>
          Aggregated statistics: create de-identified population insights (e.g.,
          growth curves). These aggregates cannot reasonably identify an
          individual.
        </li>
      </ul>

      <h2>Sharing</h2>
      <ul>
        <li>
          Service providers only: We use vendors (e.g., hosting/telemetry)
          strictly to operate GH Tracker. Contracts prohibit them from using
          data for their own purposes.
        </li>
        <li>No ads SDKs. No data brokers.</li>
        <li>
          Legal: We may disclose information if required by law, to protect
          safety, or to enforce our terms.
        </li>
      </ul>

      <h2>Retention & Deletion</h2>
      <ul>
        <li>
          Data linked to your current app ID is retained while the app ID
          remains active.
        </li>
        <li>
          <em>Reset My Data</em>: In Settings → Privacy, tap Reset My Data to
          rotate your local app ID and trigger server-side deletion of records
          linked to the old ID.
        </li>
        <li>
          Aggregates: We may retain de-identified aggregate statistics not
          linked to any app ID.
        </li>
        <li>Security logs are retained only briefly.</li>
      </ul>

      <h2>Your Choices & Rights</h2>
      <ul>
        <li>
          <em>Reset My Data</em>: rotate your app ID and request deletion of
          prior linked records.
        </li>
        <li>Export: export your entries locally (e.g., CSV) from Settings.</li>
        <li>
          Contact deletion: email privacy@GH Tracker.com with your Current App
          ID (shown in Settings) to request deletion; we'll confirm once
          complete.
        </li>
      </ul>

      <h2>Children's Privacy</h2>
      <p>
        GH Tracker is not directed to children under 13. We design for adult
        users and caregivers. If we learn we inadvertently collected information
        from a child, we will delete it. We limit our per-install identifier and
        analytics to internal operations and do not use them for behavioral
        advertising.
      </p>

      <h2>Photos & Permissions (Camera / Library)</h2>
      <ul>
        <li>
          GH Tracker may request access to your <em>Camera</em> and/or{" "}
          <em>Photos/Library</em> so you can attach images to your entries to
          track progress visually.
        </li>
        <li>
          Images are stored <em>locally on your device</em> and are{" "}
          <strong>not uploaded or sent</strong> to our servers. If your device
          is configured to back up photos, images may be included in your device
          or platform backups.
        </li>
        <li>
          You can deny or revoke permissions at any time in your device
          settings. Core tracking features (age, height, weight, dosage) remain
          available without image permissions.
        </li>
        <li>
          We do not run cloud-based image analysis and do not collect facial
          recognition or biometric templates.
        </li>
      </ul>

      <h2>Accuracy & Medical Disclaimer</h2>
      <ul>
        <li>
          Percentages, percentiles, and other metrics shown in GH Tracker may
          involve rounding or estimation and may not be exact.
        </li>
        <li>
          GH Tracker is provided for informational and record-keeping purposes
          only and is <strong>not a medical device</strong>. It is not intended
          to diagnose, treat, cure, or prevent any disease or condition.
        </li>
        <li>
          Always consult a qualified healthcare professional for medical advice,
          diagnoses, or treatment. Never disregard professional medical advice
          or delay seeking it because of something you read in the app.
        </li>
      </ul>

      <h2>Limitation of Liability & Warranty Disclaimer</h2>
      <p>
        To the maximum extent permitted by applicable law, GH Tracker and
        RedCalf provide the app "as is" and "as available," without warranties
        of any kind, whether express, implied, or statutory, including but not
        limited to implied warranties of merchantability, fitness for a
        particular purpose, title, and non-infringement. RedCalf does not
        warrant that the app will be error-free or that any defects will be
        corrected. Your use of the app is at your sole risk. In no event shall
        RedCalf be liable for any indirect, incidental, special, consequential,
        or exemplary damages, or for any loss of data, profits, goodwill, or
        other intangible losses, arising out of or relating to your use of or
        inability to use the app, even if RedCalf has been advised of the
        possibility of such damages.
      </p>

      <h2>Security</h2>
      <p>
        We use reasonable safeguards (encryption in transit, access controls).
        No method is 100% secure, but we work to protect your information.
      </p>

      <h2>International Users</h2>
      <p>
        If you access GH Tracker from outside the U.S., your data may be
        processed in the United States or other locations where our service
        providers operate.
      </p>

      <h2>Changes to This Policy</h2>
      <p>
        We may update this policy for product, legal, or operational reasons.
        We'll update the "Effective date" and, where required, notify you
        in-app.
      </p>

      <h2>Contact</h2>
      <p>
        <strong>RedCalf</strong>
      </p>
      <p>
        <strong>Email:</strong> privacy@GH Tracker.com
      </p>
      <p>
        <strong>Address:</strong> [Your Company Address]
      </p>

      <div
        style={{
          marginTop: "2rem",
          padding: "1rem",
          background: "#f6f8fa",
          borderRadius: "6px",
          border: "1px solid #e1e4e8",
        }}
      >
        <p>
          <strong>Note:</strong> This privacy policy is effective as of the date
          listed above and applies to all information collected by GH Tracker.
          By using our service, you acknowledge that you have read and
          understood this privacy policy.
        </p>
      </div>
    </div>
  );
};

export default Privacy;
